import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Link, type LucideIcon, RefreshCw, Timer, Wrench } from "lucide-react";
import { CardStatusBadge } from "@/components/custom/card-status-badge";
import {
  useCheckServerHealth,
  useGetDeploymentStatus,
  useServerAuthStatus,
} from "../api/hooks";
import { OAuthRequiredAlert } from "../components/oauth/oauth-required-alert";
import { OAuthStatusBadge } from "../components/oauth/oauth-status-badge";
import { ServerHealthIndicator } from "../list/components/health-indicator/server-health-indicator";
import { requiresOAuth, type Server } from "../types";

// Constants for timing
const HEALTH_REFETCH_INTERVAL = 30_000; // 30 seconds
const ACTIVE_DEPLOYMENT_REFETCH_INTERVAL = 2000; // 2 seconds

type ServerInfoProps = {
  server: Server;
  numTools: number;
};

export const ServerInfo = ({ server, numTools }: ServerInfoProps) => {
  const isStatic = server.binding.type === "static";
  const isManaged = server.managed;
  const {
    data: health,
    error,
    dataUpdatedAt,
  } = useCheckServerHealth({
    id: server.id,
    options: { refetchInterval: HEALTH_REFETCH_INTERVAL },
    enabled: !isManaged,
  });

  const { data: deploymentStatusData } = useGetDeploymentStatus({
    id: server.id,
    enabled: isManaged,
    activeInterval: ACTIVE_DEPLOYMENT_REFETCH_INTERVAL,
    stableInterval: HEALTH_REFETCH_INTERVAL,
  });

  const uri = server.type === "http" ? server.http?.uri : server.mcp?.uri;
  const shouldShowUri = !isManaged;
  const timeout =
    server.type === "http" ? server.http?.timeout : server.mcp?.timeout;
  const retry = server.type === "http" ? server.http?.retry : server.mcp?.retry;
  const needsOAuth = requiresOAuth(server);
  const { isAuthorized } = useServerAuthStatus(
    server.id,
    server.binding.type,
    server.type
  );
  const showAuthWarning = needsOAuth && !isAuthorized;

  return (
    <>
      {!!showAuthWarning && <OAuthRequiredAlert server={server} />}
      <Card className="bg-background">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between border-border/40 border-b pb-3">
            <div className="flex items-center gap-2">
              <ServerHealthIndicator
                dataUpdatedAt={dataUpdatedAt}
                deploymentStatus={deploymentStatusData?.status}
                error={error}
                health={health}
                isManaged={isManaged}
              />
              <CardTitle className="font-bold text-xl">{server.id}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <CardStatusBadge
                isEnabled={server.enabled}
                isStatic={isStatic}
                showEnabled={true}
              />
              <OAuthStatusBadge requiresAuth={showAuthWarning} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-1 pb-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-5">
            <InfoItem
              className="col-span-2"
              icon={Link}
              label="Endpoint"
              value={shouldShowUri ? uri : "Hosted by Arcade"}
            />

            <InfoItem icon={Wrench} label="Tools" value={`${numTools} tools`} />
            <InfoItem
              icon={Timer}
              label="Timeout"
              value={`${timeout} seconds`}
            />
            <InfoItem
              icon={RefreshCw}
              label="Retry Attempts"
              value={`${retry} attempts`}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
};

type InfoItemProps = {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  className?: string;
};

const InfoItem = ({ icon: Icon, label, value, className }: InfoItemProps) => (
  <div
    className={cn(
      "flex items-center gap-2 rounded-lg bg-background p-2",
      className
    )}
  >
    <div className="rounded-full border p-1.5">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
    </div>
    <div>
      <p className="font-medium text-xs">{label}</p>
      <p className="break-all text-muted-foreground text-xs">{value}</p>
    </div>
  </div>
);
